import { useQuery } from "@rocicorp/zero/react";
import { useEffect, useState } from "react";
import { queries } from "../../shared/queries";
import { useToast } from "./Toast";

const SERVED_VERSION_POLL_MS = 60_000;

function readReloadVersion(doc: Document): number {
  return Number(
    doc.querySelector<HTMLMetaElement>('meta[name="client-reload-version"]')
      ?.content
  );
}

const bundledVersion = readReloadVersion(document);

async function fetchServedVersion(): Promise<number> {
  const response = await fetch("/", { cache: "no-store" });
  const html = await response.text();
  return readReloadVersion(new DOMParser().parseFromString(html, "text/html"));
}

export function StaleClientToast() {
  const [tenant] = useQuery(queries.currentTenant());
  const { showToast, hideToast } = useToast();
  const [servedVersion, setServedVersion] = useState(bundledVersion);
  const announced = (tenant?.clientReloadVersion ?? 0) > bundledVersion;
  // The database is stamped before the deploy activates the new index, so only
  // prompt once a reload would actually load a newer bundle.
  const stale = announced && servedVersion > bundledVersion;

  useEffect(() => {
    if (!announced || stale) return;
    let cancelled = false;
    const check = () =>
      fetchServedVersion().then(
        (version) => {
          if (!cancelled) setServedVersion(version);
        },
        // A failed fetch is retried on the next poll.
        () => {}
      );
    void check();
    const interval = setInterval(check, SERVED_VERSION_POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [announced, stale]);

  useEffect(() => {
    if (!stale) {
      hideToast("stale-client");
      return;
    }
    showToast({
      key: "stale-client",
      icon: "refresh-cw",
      message: "A new version of 3B is available.",
      buttonLabel: "Refresh",
      onButtonClick: () => window.location.reload(),
      persist: true,
    });
  }, [stale, showToast, hideToast]);

  return null;
}
